import { useState } from 'react';

import { type User, validateSession } from '@enterprise/api';
import { isNotDefined } from '@enterprise/core';
import { useDidMount } from '@enterprise/hooks';
import type { OperationResultWithData } from '@enterprise/types';

import { heartbeatThresholdMs } from '@/constants/session-config';
import { useApplicationStore } from '@hooks/application/application-store.hook';

export const useSessionRecoveryConfiguration = () => {
  const enterpriseApi = useApplicationStore(({ enterpriseApi }) => {
    return enterpriseApi;
  });
  const lastValidatedAt = useApplicationStore(({ lastValidatedAt }) => {
    return lastValidatedAt;
  });
  const setLastValidatedAt = useApplicationStore(({ setLastValidatedAt }) => {
    return setLastValidatedAt;
  });
  const setUser = useApplicationStore(({ setUser }) => {
    return setUser;
  });
  const user = useApplicationStore(({ user }) => {
    return user;
  });

  const [recovering, setRecovering] = useState(() => {
    if (isNotDefined(user)) {
      return true;
    }

    if (isNotDefined(lastValidatedAt)) {
      return true;
    }

    const now = Date.now();

    if (lastValidatedAt > now) {
      return true;
    }

    return now - lastValidatedAt >= heartbeatThresholdMs;
  });

  useDidMount(() => {
    if (!recovering) {
      return;
    }

    const recover = async () => {
      try {
        const result: OperationResultWithData<User> = await validateSession(enterpriseApi);

        if (result.failure || isNotDefined(result.data)) {
          throw new Error('Session validation failed');
        }

        setUser(result.data);
        setLastValidatedAt(Date.now());
      } catch {
        setUser(undefined);
      } finally {
        setRecovering(false);
      }
    };

    void recover();
  });

  return { recovering };
};
